import React, { useEffect, useMemo, useRef, useState } from "react";
import { Select, TextInput, Textarea } from "flowbite-react";
import { Spinner } from "../atoms/Spinner";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  HiOutlineCheckCircle,
  HiOutlineChevronRight,
  HiOutlineSearch,
  HiOutlineX,
} from "react-icons/hi";
import { NoTranslate } from "../atoms/NoTranslate";
import { CustomButton } from "../atoms/CustomButton";
import { EmptyState } from "../atoms/EmptyState";
import { StatusBadge } from "../atoms/StatusBadge";
import { ModalShell } from "../atoms/ModalShell";
import { CharacterCounter } from "../atoms/CharacterCounter";
import { FieldLabel } from "../atoms/FieldLabel";
import SenderEmailGateBanner from "../atoms/SenderEmailGateBanner";
import useAuth from "../../hooks/useAuth";
import { useSenderEmailGate } from "../../hooks/useSenderEmailGate";
import { useCreateSigningRequest } from "../../hooks/esignature";
import { useGetSearchUsers } from "../../hooks/users";
import { useGetEntityOwnerData } from "../../hooks/entity";
import { usePermissions } from "../../utils/permissions";
import {
  ESignatureTemplate,
  CreateSigningRequestPayload,
} from "../../types/ESignatureTypes";

interface PickedUser {
  user_identification: string;
  user_first_name?: string;
  user_last_name?: string;
  user_email?: string;
  user_user_name?: string;
  user_avatar?: string;
  user_is_active?: boolean;
}

interface CreateSigningRequestModalProps {
  show: boolean;
  onClose: () => void;
  templates: ESignatureTemplate[];
  preselectedTemplateId?: string;
  preselectedTemplate?: ESignatureTemplate | null;
  preselectedUser?: PickedUser;
}

const formatUserDisplay = (u: PickedUser) => {
  const fullName = [u.user_first_name, u.user_last_name]
    .filter(Boolean)
    .join(" ");
  return fullName || u.user_user_name || u.user_email || u.user_identification;
};

const getAvatarInitials = (u: PickedUser): string => {
  if (u.user_first_name && u.user_last_name) {
    return (
      u.user_first_name.charAt(0).toUpperCase() +
      u.user_last_name.charAt(0).toUpperCase()
    );
  }
  const fallback =
    u.user_first_name ||
    u.user_last_name ||
    u.user_user_name ||
    u.user_email ||
    "";
  return fallback.charAt(0).toUpperCase();
};

const CreateSigningRequestModal: React.FC<CreateSigningRequestModalProps> = ({
  show,
  onClose,
  templates,
  preselectedTemplateId,
  preselectedTemplate,
  preselectedUser,
}) => {
  const { t } = useTranslation();
  const { customer_identification, user } = useAuth();
  const entityIdentification = user?.entity_identification || "";

  const initialTemplateId =
    preselectedTemplate?.template_id || preselectedTemplateId || "";

  const [selectedTemplateId, setSelectedTemplateId] =
    useState(initialTemplateId);
  const [selectedUser, setSelectedUser] = useState<PickedUser | null>(
    preselectedUser || null
  );
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [message, setMessage] = useState("");
  const [expiryDays, setExpiryDays] = useState<string>("30");
  const [signerError, setSignerError] = useState(false);
  const [requirePhoneVerification, setRequirePhoneVerification] =
    useState(false);
  const signerSectionRef = useRef<HTMLDivElement | null>(null);

  const createMutation = useCreateSigningRequest();

  const permissions = usePermissions(user);
  const hasPhoneVerificationPlan = permissions.hasFeatureAccess(
    "PHONE_NUMBER_VALIDATION"
  );
  const { data: smsCreditData, isLoading: isLoadingSmsCredit } =
    useGetEntityOwnerData(
      customer_identification,
      entityIdentification,
      "sms_credit",
      {
        enabled:
          show &&
          hasPhoneVerificationPlan &&
          !!customer_identification &&
          !!entityIdentification,
      }
    );
  const smsCredit = smsCreditData?.entity_other_data?.sms_credit ?? 0;
  const phoneVerificationBlock = !hasPhoneVerificationPlan
    ? "phoneVerificationNoPlan"
    : !isLoadingSmsCredit && smsCredit < 1
    ? "phoneVerificationNoCredit"
    : null;
  const canRequirePhoneVerification =
    !phoneVerificationBlock && !isLoadingSmsCredit;

  const senderGate = useSenderEmailGate(
    customer_identification,
    entityIdentification,
    { enabled: show }
  );

  useEffect(() => {
    if (show) {
      const templateId =
        preselectedTemplate?.template_id || preselectedTemplateId || "";
      setSelectedTemplateId(templateId);
      setSelectedUser(preselectedUser || null);
      setSearchInput("");
      setSearchQuery("");
      setMessage("");
      setExpiryDays("30");
      setSignerError(false);
      setRequirePhoneVerification(false);
    }
  }, [show, preselectedTemplateId, preselectedTemplate, preselectedUser]);

  const handleSearch = () => {
    const trimmed = searchInput.trim();
    if (trimmed.length >= 5) {
      setSearchQuery(trimmed);
    }
  };

  const handleSearchKeyDown = (key: string) => {
    if (key === "Enter") handleSearch();
  };

  const { data: searchData, isFetching: isSearching } = useGetSearchUsers({
    customer_identification,
    entityIdentification,
    query: searchQuery,
    limit: 10,
    pageParam: 0,
  });

  const searchResults: PickedUser[] = useMemo(() => {
    if (!searchData) return [];
    if (Array.isArray(searchData)) return searchData;
    return searchData.users ?? [];
  }, [searchData]);

  useEffect(() => {
    if (selectedUser) setSignerError(false);
  }, [selectedUser]);

  const isValid =
    !!selectedTemplateId &&
    !!selectedUser?.user_identification &&
    (expiryDays === "" ||
      (Number(expiryDays) >= 1 && Number(expiryDays) <= 180));

  const handleSubmit = () => {
    if (!selectedUser) {
      setSignerError(true);
      toast.error(
        t("esignature.signingRequest.modal.signerRequired") as string
      );
      signerSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }
    if (!isValid) return;
    if (!senderGate.canSend) {
      const msg =
        senderGate.reason === "default"
          ? t(
              "dashboard.entities.emailSenderValidation.defaultDomainBlockMessage"
            )
          : t(
              "dashboard.entities.emailSenderValidation.unverifiedBlockMessage"
            );
      toast.error(msg as string);
      return;
    }

    const payload: CreateSigningRequestPayload = {
      template_id: selectedTemplateId,
      signer_user_identification: selectedUser.user_identification,
      ...(message.trim() ? { message: message.trim() } : {}),
      ...(expiryDays ? { expiry_days: Number(expiryDays) } : {}),
      ...(requirePhoneVerification && canRequirePhoneVerification
        ? { require_phone_verification: true }
        : {}),
    };

    createMutation.mutate(
      {
        customer_identification,
        entityIdentification,
        data: payload,
      },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  return (
    <ModalShell
      show={show}
      onClose={onClose}
      size="lg"
      title={t("esignature.signingRequest.modal.title")}
      footer={
        <>
          <CustomButton
            title={t("common.cancel")}
            onClick={onClose}
            variant="secondary"
          />
          <CustomButton
            title={t("esignature.signingRequest.modal.submitButton")}
            onClick={handleSubmit}
            variant="primary"
            isLoading={createMutation.isLoading}
            disabled={
              !selectedTemplateId ||
              createMutation.isLoading ||
              !senderGate.canSend
            }
          />
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {!senderGate.canSend && !senderGate.isLoading && (
          <SenderEmailGateBanner gate={senderGate} />
        )}
        <div>
          <FieldLabel>
            {t("esignature.signingRequest.modal.templateLabel")}
          </FieldLabel>
          <Select
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
            className="w-full"
          >
            <option value="">
              {t("esignature.signingRequest.modal.templatePlaceholder")}
            </option>
            {templates.map((tmpl) => (
              <option key={tmpl.template_id} value={tmpl.template_id}>
                {tmpl.name}
              </option>
            ))}
          </Select>
        </div>

        <div ref={signerSectionRef}>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            {t("esignature.signingRequest.modal.signerLabel")}
          </label>

          {selectedUser ? (
            <div className="flex items-center justify-between bg-white border border-green-300 rounded-lg p-3 gap-2">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {selectedUser.user_avatar ? (
                  <div className="relative flex-shrink-0">
                    <img
                      src={selectedUser.user_avatar}
                      alt=""
                      className="w-9 h-9 rounded-full object-cover border-2 border-gray-200"
                    />
                    <HiOutlineCheckCircle
                      size={16}
                      className="absolute -bottom-0.5 -right-0.5 text-green-600 bg-white rounded-full"
                    />
                  </div>
                ) : (
                  <div className="relative flex-shrink-0">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold text-sm">
                      <NoTranslate>
                        {getAvatarInitials(selectedUser)}
                      </NoTranslate>
                    </div>
                    <HiOutlineCheckCircle
                      size={16}
                      className="absolute -bottom-0.5 -right-0.5 text-green-600 bg-white rounded-full"
                    />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {formatUserDisplay(selectedUser)}
                    </p>
                    {selectedUser.user_is_active !== undefined &&
                      (selectedUser.user_is_active ? (
                        <StatusBadge color="success" className="shrink-0">
                          {t("dashboard.entities.viewEntityUser.status-01")}
                        </StatusBadge>
                      ) : (
                        <StatusBadge color="warning" className="shrink-0">
                          {t("dashboard.entities.viewEntityUser.status-02")}
                        </StatusBadge>
                      ))}
                  </div>
                  {selectedUser.user_email && (
                    <p className="text-xs text-gray-500 truncate">
                      {selectedUser.user_email}
                    </p>
                  )}
                </div>
              </div>
              {!preselectedUser && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedUser(null);
                    setSignerError(false);
                  }}
                  className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                  title={t("common.remove") as string}
                >
                  <HiOutlineX size={18} />
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="flex gap-2">
                <div className="flex-1">
                  <TextInput
                    type="text"
                    icon={HiOutlineSearch}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => handleSearchKeyDown(e.key)}
                    color={signerError ? "failure" : undefined}
                    className="w-full"
                    placeholder={
                      t(
                        "esignature.signingRequest.modal.signerSearchPlaceholder"
                      ) as string
                    }
                    data-testid="esignature-signer-search-input"
                  />
                </div>
                <CustomButton
                  title={t("common.search") as string}
                  onClick={handleSearch}
                  variant="primary"
                  disabled={searchInput.trim().length < 5 || isSearching}
                  isLoading={isSearching}
                  /*
                   * Plus de `max-h-[42px]`: la ligne est un flex qui etire ses
                   * enfants, le bouton prenait donc la hauteur du champ, sauf
                   * sous `md` ou le champ mesure 44px et ou ce plafond le
                   * bloquait a 42, soit 1px de decalage en haut et en bas.
                   */
                />
              </div>
              {signerError ? (
                <p className="mt-1 text-xs text-red-600 font-medium">
                  {t("esignature.signingRequest.modal.signerRequired")}
                </p>
              ) : (
                <p className="mt-1 text-xs text-gray-500">
                  {t("esignature.signingRequest.modal.signerSearchHint")}
                </p>
              )}

              {searchQuery.length >= 5 && (
                <>
                  {!isSearching && searchResults.length > 0 && (
                    <p className="mt-3 mb-1.5 text-xs text-gray-500">
                      {t("esignature.signingRequest.modal.signerResultsCount", {
                        count: searchResults.length,
                      })}
                    </p>
                  )}
                  <div className="mt-2 rounded-lg max-h-56 overflow-y-auto bg-gray-50 border border-gray-200">
                    {isSearching ? (
                      <div className="flex items-center justify-center py-4">
                        <Spinner size="sm" />
                      </div>
                    ) : searchResults.length === 0 ? (
                      <EmptyState
                        className="py-6"
                        icon={<HiOutlineSearch size={24} />}
                        title={t(
                          "esignature.signingRequest.modal.signerNoResults"
                        )}
                      />
                    ) : (
                      <ul
                        className="divide-y divide-gray-200"
                        data-testid="esignature-signer-search-results"
                      >
                        {searchResults.map((u) => (
                          <li key={u.user_identification}>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedUser(u);
                                setSearchInput("");
                                setSearchQuery("");
                                setSignerError(false);
                              }}
                              className="group w-full text-left px-3 py-2.5 cursor-pointer hover:bg-blue-50 focus:bg-blue-50 focus:outline-none transition-colors flex items-center gap-3"
                              data-testid="esignature-signer-search-result"
                              data-user-email={u.user_email || ""}
                            >
                              {u.user_avatar ? (
                                <img
                                  src={u.user_avatar}
                                  alt=""
                                  className="w-8 h-8 rounded-full object-cover border-2 border-gray-200 flex-shrink-0"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
                                  <NoTranslate>
                                    {getAvatarInitials(u)}
                                  </NoTranslate>
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {formatUserDisplay(u)}
                                  </p>
                                  {u.user_is_active !== undefined &&
                                    (u.user_is_active ? (
                                      <StatusBadge
                                        color="success"
                                        className="shrink-0"
                                      >
                                        {t(
                                          "dashboard.entities.viewEntityUser.status-01"
                                        )}
                                      </StatusBadge>
                                    ) : (
                                      <StatusBadge
                                        color="warning"
                                        className="shrink-0"
                                      >
                                        {t(
                                          "dashboard.entities.viewEntityUser.status-02"
                                        )}
                                      </StatusBadge>
                                    ))}
                                </div>
                                {u.user_email && (
                                  <p className="text-xs text-gray-500 truncate">
                                    {u.user_email}
                                  </p>
                                )}
                              </div>
                              <HiOutlineChevronRight
                                size={18}
                                className="text-gray-400 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all flex-shrink-0"
                              />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div>
          <FieldLabel>
            {t("esignature.signingRequest.modal.messageLabel")}
          </FieldLabel>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            maxLength={300}
            className="w-full"
            placeholder={
              t("esignature.signingRequest.modal.messagePlaceholder") as string
            }
          />
          <CharacterCounter
            currentLength={message.length}
            maxLength={300}
            className="mt-1"
          />
        </div>

        <div>
          <div className="flex items-center gap-3">
            <FieldLabel htmlFor="esignature-expiry-input" className="mb-0">
              {t("esignature.signingRequest.modal.expiryLabel")}
            </FieldLabel>
            <div className="relative w-32 flex-shrink-0">
              <TextInput
                id="esignature-expiry-input"
                type="number"
                min={1}
                max={180}
                value={expiryDays}
                onChange={(e) => setExpiryDays(e.target.value)}
                className="w-full"
                theme={{ field: { input: { withShadow: { off: "pr-12" } } } }}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 pointer-events-none">
                {t("esignature.signingRequest.modal.expiryUnit")}
              </span>
            </div>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {t("esignature.signingRequest.modal.expiryHint")}
          </p>
        </div>

        <div>
          <label
            className={`flex items-start gap-3 ${
              canRequirePhoneVerification
                ? "cursor-pointer"
                : "cursor-not-allowed"
            }`}
          >
            <input
              type="checkbox"
              checked={requirePhoneVerification && canRequirePhoneVerification}
              disabled={!canRequirePhoneVerification}
              onChange={(e) => setRequirePhoneVerification(e.target.checked)}
              className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
              data-testid="esignature-require-phone-verification"
            />
            <span>
              <span
                className={`block text-sm font-medium ${
                  canRequirePhoneVerification
                    ? "text-gray-700"
                    : "text-gray-400"
                }`}
              >
                {t("esignature.signingRequest.modal.phoneVerificationLabel")}
              </span>
              <span className="block mt-0.5 text-xs text-gray-500">
                {t(
                  `esignature.signingRequest.modal.${
                    phoneVerificationBlock ?? "phoneVerificationHint"
                  }`
                )}
              </span>
            </span>
          </label>
        </div>
      </div>
    </ModalShell>
  );
};

export default CreateSigningRequestModal;
